// File: src/components/SessionPanel/hooks/useSummary.ts
import { useCallback, useState } from "react";
import type { UpdateEvent } from "../../../types/domain";
import { postSummary } from "../api";

export function useSummary(opts: {
  API_BASE: string;
  getToken: () => Promise<string | null>;
  ensureSession: () => Promise<string>;
  sessionId?: string;
  bubbles: UpdateEvent[];
  pinnedSeqs: Set<number>;
  includePinnedInSummary: boolean;
  /** Selected summarize prompt id (picked in the prompt library); the
   * backend falls back to the seeded default when absent. */
  promptId?: string;
  onSummary?: (s: string) => void;
  setError: (s: string | null) => void;
  setBusy: (b: boolean) => void;
}) {
  const {
    API_BASE,
    getToken,
    ensureSession,
    sessionId,
    bubbles,
    pinnedSeqs,
    includePinnedInSummary,
    promptId,
    onSummary,
    setError,
    setBusy,
  } = opts;

  const [summary, setSummary] = useState<string>("");

  const summarize = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const sid = sessionId ?? (await ensureSession());

      const lines = bubbles
        .map((b) => `${b.speaker || "?"}: ${(b.text ?? "").trim()}`)
        .filter(Boolean);

      const pinnedLines = bubbles
        .filter((b) => pinnedSeqs.has(b.seq))
        .map((b) => `${b.speaker || "?"}: ${(b.text ?? "").trim()}`);

      const s = await postSummary(
        getToken,
        API_BASE,
        sid,
        lines.join("\n"),
        includePinnedInSummary ? pinnedLines : [],
        promptId
      );

      setSummary(s);
      onSummary?.(s);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [
    API_BASE,
    getToken,
    ensureSession,
    sessionId,
    bubbles,
    pinnedSeqs,
    includePinnedInSummary,
    promptId,
    onSummary,
    setError,
    setBusy,
  ]);

  return { summary, setSummary, summarize } as const;
}

export {};
